import type { LightSource } from './lighting';
import type { Camera } from '../types';
import { worldToScreen } from '../utils';

// --- Types ---

/** Light source with color data, for color overlay rendering */
export interface LightColorSource {
  x: number;            // world-space
  y: number;            // world-space
  brightRadius: number; // canvas pixels (world-space)
  dimRadius: number;    // canvas pixels (world-space)
  color: string;        // hex color (e.g. '#FFA500')
}

// --- Utilities ---

/**
 * Parse a hex color string to RGB components.
 * Supports 3-char (#RGB) and 6-char (#RRGGBB) formats.
 * Returns null for invalid input.
 */
export function hexToRgb(hex: string): { r: number; g: number; b: number } | null {
  if (!hex || hex[0] !== '#') return null;

  let r: number, g: number, b: number;

  if (hex.length === 4) {
    // 3-char: #RGB -> #RRGGBB
    const r1 = parseInt(hex[1], 16);
    const g1 = parseInt(hex[2], 16);
    const b1 = parseInt(hex[3], 16);
    if (isNaN(r1) || isNaN(g1) || isNaN(b1)) return null;
    r = r1 * 17; // 0xF -> 0xFF
    g = g1 * 17;
    b = b1 * 17;
  } else if (hex.length === 7) {
    // 6-char: #RRGGBB
    r = parseInt(hex.slice(1, 3), 16);
    g = parseInt(hex.slice(3, 5), 16);
    b = parseInt(hex.slice(5, 7), 16);
    if (isNaN(r) || isNaN(g) || isNaN(b)) return null;
  } else {
    return null;
  }

  return { r, g, b };
}

// --- Source filtering ---

/**
 * Extract colored sources from LightSource[] for overlay rendering.
 * Filters out global sources (handled separately) and sources without color.
 */
export function collectColoredSources(sources: LightSource[]): LightColorSource[] {
  const result: LightColorSource[] = [];

  for (const src of sources) {
    // Skip global sources — handled via globalColor parameter
    if (src.type === 'global') continue;
    // Skip sources without color
    if (!src.color) continue;

    result.push({
      x: src.x,
      y: src.y,
      brightRadius: src.brightRadius,
      dimRadius: src.dimRadius,
      color: src.color,
    });
  }

  return result;
}

// --- Main render function ---

/**
 * Render light color overlay onto an offscreen canvas.
 *
 * Draws radial gradients for each colored light source using additive blending.
 * Optionally renders a global illumination color tint as a base layer.
 *
 * The result should be composited onto the fog canvas with `source-atop` to
 * only show color where fog pixels exist (the transition edges).
 *
 * @param ctx - offscreen canvas 2D context (will be cleared)
 * @param sources - colored light sources (non-global, with color)
 * @param globalColor - GlobalIllumination.color (undefined = no tint)
 * @param globalLevel - GlobalIllumination.level ('bright'|'dim'|'dark')
 * @param cam - current camera (for world-to-screen conversion)
 * @param dpr - device pixel ratio
 * @param cssW - canvas CSS width
 * @param cssH - canvas CSS height
 */
export function renderLightColorOverlay(
  ctx: CanvasRenderingContext2D,
  sources: LightColorSource[],
  globalColor: string | undefined,
  globalLevel: 'bright' | 'dim' | 'dark',
  cam: Camera,
  dpr: number,
  cssW: number,
  cssH: number,
): void {
  ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
  ctx.clearRect(0, 0, cssW, cssH);

  // --- Global illumination color tint ---
  // Only render if globalColor is set and level is NOT dark (dark = no ambient light)
  if (globalColor && globalLevel !== 'dark') {
    const rgb = hexToRgb(globalColor);
    if (rgb) {
      ctx.globalCompositeOperation = 'source-over';
      ctx.fillStyle = `rgba(${rgb.r}, ${rgb.g}, ${rgb.b}, 0.10)`;
      ctx.fillRect(0, 0, cssW, cssH);
    }
  }

  // --- Individual light source gradients ---
  if (sources.length === 0) return;

  // Additive blending for overlapping colored lights
  ctx.globalCompositeOperation = 'lighter';

  for (const src of sources) {
    const rgb = hexToRgb(src.color);
    if (!rgb) continue;

    // Convert source position to screen coordinates
    const screenPos = worldToScreen(src.x, src.y, cam);
    // Convert radii to screen pixels
    const screenBrightR = src.brightRadius * cam.zoom;
    const screenDimR = src.dimRadius * cam.zoom;

    // Skip if dim radius is too small to be visible
    if (screenDimR < 1) continue;

    // Create radial gradient: center -> bright edge -> dim edge
    const gradient = ctx.createRadialGradient(
      screenPos.x, screenPos.y, 0,
      screenPos.x, screenPos.y, screenDimR,
    );

    // Gradient stops:
    // 0.0 (center) -> brightRadius: full color at 25% opacity
    // brightRadius -> dimRadius: fade from 25% to 0%
    const brightStop = screenDimR > 0 ? screenBrightR / screenDimR : 0;
    const clampedBrightStop = Math.min(Math.max(brightStop, 0), 1);

    gradient.addColorStop(0, `rgba(${rgb.r}, ${rgb.g}, ${rgb.b}, 0.25)`);
    gradient.addColorStop(clampedBrightStop, `rgba(${rgb.r}, ${rgb.g}, ${rgb.b}, 0.25)`);
    gradient.addColorStop(1, `rgba(${rgb.r}, ${rgb.g}, ${rgb.b}, 0.0)`);

    ctx.fillStyle = gradient;
    ctx.beginPath();
    ctx.arc(screenPos.x, screenPos.y, screenDimR, 0, Math.PI * 2);
    ctx.fill();
  }

  // Reset composite operation
  ctx.globalCompositeOperation = 'source-over';
}
